/**
 * Error processing utilities for converting API error responses into form field errors.
 * Originally from @avamae/process-errors package.
 */
import toPath from "lodash/toPath";
import set from "lodash/set";

function identity<T>(x: T): T {
  return x;
}

/**
 * Represents an error response from the API with field name and message.
 */
export type ErrorResponse = {
  fieldName: string;
  messageText: string;
};

/**
 * Converts a PascalCase path string to camelCase path array.
 * @param pathstring - Dot-separated path string in PascalCase (e.g., "User.FirstName")
 * @returns Array of path segments in camelCase (e.g., ["user", "firstName"])
 */
export function dePascalPath(pathstring: string): string[] {
  return toPath(pathstring).map((seg) => seg.slice(0, 1).toLowerCase() + seg.slice(1));
}

/**
 * Processes an array of API error responses into a nested error object suitable for form validation.
 * Converts PascalCase field names to camelCase and optionally translates error codes.
 *
 * @param errors - Array of error responses from the API
 * @param translateErrorCode - Optional function to translate error message codes
 * @returns Nested error object with camelCase field paths as keys and error messages as values
 */
export function processErrors(
  errors: ErrorResponse[],
  translateErrorCode?: (code: string) => string
) {
  const translate = translateErrorCode || identity;
  const dePascalled = errors.map((e) => ({
    ...e,
    fieldName: dePascalPath(e.fieldName),
  }));

  const errorObj: any = {};

  dePascalled.forEach((res) => set(errorObj, res.fieldName, translate(res.messageText)));

  return errorObj;
}
